/**
 * Où en est la correction d'une copie pendant qu'on l'attend.
 *
 * Aucune règle d'éligibilité ici : à l'envoi de la copie, le backend dit si Sam
 * la corrigera une fois notée (`correction_wait`, mêmes portes que le
 * déclenchement de la correction). Sans ce oui, il n'y a pas d'attente du tout.
 *
 * Déplacé tel quel de `student/activity/execute/` : seuls des types sont ajoutés.
 */

type CorrectionLog = { action?: string | null; work_attachment?: string | null } | null | undefined
type CorrectionActivity = { activity_logs?: CorrectionLog[] | null } | null | undefined

// La copie rendue : les photos de l'étape, pas l'image de correction, qui vit
// sur un autre journal de l'activité.
export const submittedWorkImages = (activity: CorrectionActivity) =>
    (activity?.activity_logs ?? [])
        .filter((log) => log?.action === "step_media_sent" && log?.work_attachment)
        .map((log) => ({ uri: log!.work_attachment as string, height: 100, width: 100 }))

// Notée, la copie attend encore Sam tant que sa correction n'a pas d'issue. Le
// statut vide en fait partie : le backend valide la copie un instant avant de
// lancer la correction.
const STILL_COMING: (string | null | undefined)[] = [null, undefined, "generating"]

// Ce qu'on dit à l'élève quand l'attente prend fin sans correction : une
// attente commencée ne s'arrête jamais sans un mot.
const endingTextFor = (correctionStatus: string | null | undefined, timedOut: boolean | undefined) => {
    if (correctionStatus === "skipped_rate_limit") {
        return "Sam a déjà corrigé beaucoup de tes copies aujourd'hui. Il reprend demain."
    }
    if (correctionStatus === "failed") {
        return "Sam n'a pas réussi à corriger cette copie."
    }
    return timedOut ? "Ta correction prend plus de temps que prévu. Tu la retrouveras dans ton activité." : null
}

// Copie refusée à la notation, ou restée sans énoncé : rien à attendre.
const NO_CORRECTION = "Sam n'a pas de correction pour cette copie."

export type CorrectionFlowInput = {
    reviewStatus?: string | null
    correctionStatus?: string | null
    timedOut?: boolean
}

export type CorrectionFlow = { awaiting: boolean; completed: boolean; endingText: string | null }

export const deriveCorrectionFlow = ({
    reviewStatus,
    correctionStatus,
    timedOut,
}: CorrectionFlowInput): CorrectionFlow => {
    if (correctionStatus === "completed") {
        return { awaiting: false, completed: true, endingText: null }
    }

    const endingText = endingTextFor(correctionStatus, timedOut)
    if (endingText) {
        return { awaiting: false, completed: false, endingText }
    }

    const awaiting =
        reviewStatus === "pending_review" || (reviewStatus === "validated" && STILL_COMING.includes(correctionStatus))

    return { awaiting, completed: false, endingText: awaiting ? null : NO_CORRECTION }
}
